/**
 * Phase 19 / Task 19.2 (DEC-325) — the Operations dashboard (master spec §22,
 * 19-PLAN.md §1a). Dispatch managers, dispatchers and tenant admins (D-2).
 *
 * Live tiles refresh every minute and use the dispatcher's own timezone for
 * "today" (D-8). The driver table covers a chosen date range in the company's
 * timezone: days the nightly rollup has covered come from stored history, and
 * today is always computed live.
 */

import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useApiOpts } from '@lodex-pro/web-shared';
import { useUserTimezone } from '../lib/use-user-timezone';
import { formatTime } from '../lib/timestamp';
import { useOperationsDashboard } from '../features/dashboard/use-operations-dashboard';
import { DashboardTabs } from '../features/dashboard/DashboardTabs';
import { DashboardExport } from '../features/dashboard/DashboardExport';
import {
  DriverPerformanceTable,
  type DriverExtraColumn,
} from '../features/dashboard/DriverPerformanceTable';
import {
  complianceFieldLabel,
  daysRemainingLabel,
  formatBbl,
  formatDay,
  presetRange,
  type RangePreset,
} from '../features/dashboard/dashboard-format';
import { formatNumber } from '@lodex-pro/shared/number-format';

/**
 * The plug-in slot for the driver table's further columns (DEC-325 D-5):
 * dispatcher completions, sync nudges, miles and on-time % land here as their
 * services ship. Empty until then — no placeholder columns.
 */
const DRIVER_EXTRA_COLUMNS: readonly DriverExtraColumn[] = [];

/** A whole panel plugged into the page below Compliance (e.g. disposal-recommendation acceptance, §54). */
interface OpsPanel {
  readonly id: string;
  readonly render: () => React.ReactNode;
}

/** The plug-in slot for whole Operations panels (DEC-325 D-5). Empty until their services ship. */
const OPS_PANELS: readonly OpsPanel[] = [];

const sectionStyle: React.CSSProperties = {
  borderRadius: 'var(--radius-card)',
  padding: '12px 16px',
  backgroundColor: 'var(--color-bg-secondary)',
};

const sectionTitle: React.CSSProperties = {
  margin: '0 0 10px',
  fontSize: '13px',
  fontWeight: 700,
  color: 'var(--color-text-secondary)',
};

const mutedStyle: React.CSSProperties = { fontSize: '12px', color: 'var(--color-text-tertiary)' };

const PRESETS: readonly { id: RangePreset; label: string }[] = [
  { id: 'TODAY', label: 'Today' },
  { id: 'LAST_7', label: 'Last 7 days' },
  { id: 'LAST_30', label: 'Last 30 days' },
];

function Tile({
  label,
  value,
  sub,
  testId,
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  testId: string;
}): React.JSX.Element {
  return (
    <div
      data-testid={testId}
      style={{
        ...sectionStyle,
        minWidth: '140px',
        flex: '1 1 140px',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
      }}
    >
      <span style={mutedStyle}>{label}</span>
      <span style={{ fontSize: '24px', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </span>
      {sub !== undefined && <span style={mutedStyle}>{sub}</span>}
    </div>
  );
}

function AttentionRow({
  label,
  count,
  actionLabel,
  onAction,
  testId,
}: {
  label: string;
  count: number;
  actionLabel?: string;
  onAction?: () => void;
  testId: string;
}): React.JSX.Element {
  const hot = count > 0;
  return (
    <div
      data-testid={testId}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        padding: '8px 0',
        borderBottom: '1px solid var(--color-border-secondary)',
        fontSize: '14px',
      }}
    >
      <span>
        <strong
          style={{
            color: hot ? 'var(--color-warning)' : 'var(--color-text-tertiary)',
            marginRight: '8px',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {formatNumber(count)}
        </strong>
        {label}
      </span>
      {hot && actionLabel !== undefined && onAction !== undefined && (
        <button
          type="button"
          onClick={onAction}
          style={{
            minHeight: '36px',
            padding: '0 12px',
            borderRadius: 'var(--radius-pill)',
            border: '1px solid var(--color-border-primary)',
            background: 'var(--color-bg-tertiary)',
            color: 'var(--color-text-primary)',
            cursor: 'pointer',
          }}
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}

/** CSV choices — the API's operationsTables names; the first is its default. */
const OPERATIONS_EXPORT_TABLES = [
  { name: 'drivers', label: 'Driver performance' },
  { name: 'live', label: 'Live tiles' },
  { name: 'compliance', label: 'Compliance' },
] as const;

export function DashboardPage(): React.JSX.Element {
  const apiOpts = useApiOpts();
  const timeZone = useUserTimezone();
  const navigate = useNavigate();
  const [preset, setPreset] = React.useState<RangePreset | 'CUSTOM'>('LAST_7');
  const [range, setRange] = React.useState(() => presetRange('LAST_7', timeZone));

  const { data, isLoading, isError } = useOperationsDashboard(
    apiOpts,
    timeZone,
    range.from,
    range.to,
  );

  const choosePreset = (p: RangePreset): void => {
    setPreset(p);
    setRange(presetRange(p, timeZone));
  };
  const setCustom = (key: 'from' | 'to', value: string): void => {
    if (value === '') return;
    setPreset('CUSTOM');
    setRange((r) => ({ ...r, [key]: value }));
  };

  return (
    <div
      data-print-root
      style={{
        height: '100%',
        overflowY: 'auto',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        backgroundColor: 'var(--color-bg-primary)',
        color: 'var(--color-text-primary)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h2 style={{ margin: 0, fontSize: '18px' }}>Operations dashboard</h2>
          <DashboardTabs />
        </div>
        {data !== undefined && (
          <span style={mutedStyle} data-testid="dashboard-updated">
            Updated {formatTime(data.generatedAt, timeZone)}
          </span>
        )}
      </div>
      <DashboardExport
        path="/v1/dashboards/operations"
        params={{ tz: timeZone, from: range.from, to: range.to }}
        slug="operations"
        tables={OPERATIONS_EXPORT_TABLES}
      />

      {isLoading && <p style={mutedStyle}>Loading…</p>}
      {isError && (
        <p role="alert" style={{ color: 'var(--color-danger)' }}>
          The dashboard could not be loaded. It will retry automatically.
        </p>
      )}

      {data !== undefined && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            <Tile
              testId="tile-drivers"
              label="Drivers on shift"
              value={data.live.drivers.onShift}
              sub={`${String(data.live.drivers.paused)} paused · ${String(data.live.drivers.offShift)} off shift`}
            />
            <Tile
              testId="tile-unassigned"
              label="Unassigned jobs"
              value={data.live.jobs.unassigned}
            />
            <Tile testId="tile-not-started" label="Not started" value={data.live.jobs.notStarted} />
            <Tile testId="tile-in-progress" label="In progress" value={data.live.jobs.inProgress} />
            <Tile
              testId="tile-completed"
              label="Completed today"
              value={data.live.completedToday}
            />
            <Tile testId="tile-bbl" label="BBL today" value={formatBbl(data.live.bblToday)} />
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            <section style={{ ...sectionStyle, flex: '1 1 320px' }}>
              <h3 style={sectionTitle}>Needs attention</h3>
              <AttentionRow
                testId="attention-in-review"
                label="tickets in review"
                count={data.live.attention.inReview}
              />
              <AttentionRow
                testId="attention-stuck"
                label="stuck jobs"
                count={data.live.attention.stuck}
                actionLabel="Open stuck jobs"
                onAction={(): void => void navigate('/stuck-jobs')}
              />
              <AttentionRow
                testId="attention-sync"
                label="drivers on shift with a sync problem"
                count={data.live.attention.syncProblems}
                actionLabel="Open sync"
                onAction={(): void => void navigate('/sync')}
              />
            </section>

            <section style={{ ...sectionStyle, flex: '1 1 320px' }} data-testid="compliance">
              <h3 style={sectionTitle}>Compliance</h3>
              <p style={{ ...mutedStyle, marginTop: 0 }}>
                {data.compliance.expired} expired · {data.compliance.expiringSoon} expiring within{' '}
                {data.compliance.withinDays} days
              </p>
              {data.compliance.items.length === 0 ? (
                <p style={mutedStyle}>Nothing expired or expiring soon.</p>
              ) : (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {data.compliance.items.map((i) => (
                    <li
                      key={`${i.kind}-${i.id}-${i.field}`}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '12px',
                        padding: '6px 0',
                        borderBottom: '1px solid var(--color-border-secondary)',
                        fontSize: '13px',
                      }}
                    >
                      <span>
                        {i.kind === 'TRUCK' ? `Truck ${i.name}` : i.name} —{' '}
                        {complianceFieldLabel(i.field)}
                      </span>
                      <span
                        style={{
                          whiteSpace: 'nowrap',
                          color:
                            i.daysRemaining < 0 ? 'var(--color-danger)' : 'var(--color-warning)',
                        }}
                        title={formatDay(i.expiresOn)}
                      >
                        {daysRemainingLabel(i.daysRemaining)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          {OPS_PANELS.map((p) => (
            <React.Fragment key={p.id}>{p.render()}</React.Fragment>
          ))}
        </>
      )}

      <section style={sectionStyle}>
        <h3 style={sectionTitle}>Driver performance</h3>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            alignItems: 'center',
            marginBottom: '8px',
          }}
        >
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              aria-pressed={preset === p.id}
              onClick={(): void => choosePreset(p.id)}
              style={{
                minHeight: '36px',
                padding: '0 12px',
                borderRadius: 'var(--radius-pill)',
                border: '1px solid var(--color-border-primary)',
                background:
                  preset === p.id ? 'var(--color-accent-blue)' : 'var(--color-bg-tertiary)',
                color: preset === p.id ? 'var(--color-on-accent)' : 'var(--color-text-primary)',
                cursor: 'pointer',
              }}
            >
              {p.label}
            </button>
          ))}
          <label style={mutedStyle}>
            From{' '}
            <input
              type="date"
              value={range.from}
              max={range.to}
              onChange={(e): void => setCustom('from', e.target.value)}
            />
          </label>
          <label style={mutedStyle}>
            To{' '}
            <input
              type="date"
              value={range.to}
              min={range.from}
              onChange={(e): void => setCustom('to', e.target.value)}
            />
          </label>
        </div>
        {data !== undefined && (
          <>
            <p style={{ ...mutedStyle, marginTop: 0 }} data-testid="history-note">
              {data.drivers.history.lastRunAt === null
                ? 'History has not been built yet, so every day is computed live.'
                : `History updated ${formatTime(data.drivers.history.lastRunAt, timeZone)}` +
                  (data.drivers.history.coveredThrough === null
                    ? '.'
                    : ` and covers through ${formatDay(data.drivers.history.coveredThrough)}; later days are live.`)}
            </p>
            <DriverPerformanceTable rows={data.drivers.rows} extraColumns={DRIVER_EXTRA_COLUMNS} />
          </>
        )}
      </section>
    </div>
  );
}
